import type { ComponentType } from 'react';
import { useEffect, useMemo } from 'react';

import useFeatureSwitch from 'hooks/useFeatureSwitch/useFeatureSwitch';
import type { FeatureFlag } from 'hooks/useFeatureSwitch/useIsFeatureOn';
import type { ProjectResource } from 'hooks/useProjectResources';
import { useProjectResource, useUserAccess } from 'hooks/useProjectResources';
import { useProjectSettings } from 'hooks/useProjectSettings';
import useSampleProject from 'hooks/useSampleProjects/useSampleProject';
import useTemplateEngineUser from 'hooks/useTemplateEngineUser/useTemplateEngineUser';
import ErrorPage, { ErrorStatus } from 'screens/ErrorPage';
import type { ProjectScreenProps } from 'screens/types';
import { isTemplateEngineScreenAllowed } from './templateEngineScreens';
import { syncStore } from './useViewMode';

export interface WithPermissionsOptions {
  screenKey?: string;
}

export default function withPermissions<T extends {}>(
  Component: ComponentType<T>,
  resource?:
    | ProjectResource
    | ProjectResource[]
    | ((props: T) => ProjectResource | ProjectResource[]),
  feature?: FeatureFlag | FeatureFlag[] | ((props: T) => FeatureFlag),
  options?: WithPermissionsOptions,
) {
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing complex logic
  const WrappedComponent: ComponentType<T> = props => {
    const {
      isProjectResourceEnabled,
      isFetchingProjectFeatures,
      didProjectFeaturesError,
    } = useProjectResource();
    const { isFeatureOn, isLoadingFeatures, didFeaturesError } =
      useFeatureSwitch();
    const { inSampleProject } = useSampleProject();
    const { isTemplateEngineEnabled } = useProjectSettings();
    const { isTemplateEngineUser } = useTemplateEngineUser();
    const { isInternalUser } = useUserAccess();

    const screenProps = props as unknown as Partial<ProjectScreenProps>;
    const previewValue = screenProps.preview;
    const readonlyValue = screenProps.readonly;

    const resolvedResourceForSync = useMemo(() => {
      if (!resource) {
        return null;
      }
      const resolved =
        typeof resource === 'function' ? resource(props) : resource;
      return Array.isArray(resolved) ? resolved[0] : resolved;
      // biome-ignore lint/correctness/useExhaustiveDependencies: existing
    }, [resource, props]);

    useEffect(() => {
      if (resolvedResourceForSync) {
        syncStore({
          resource: resolvedResourceForSync,
          previewValue,
          readonlyValue,
        });
      }
    }, [resolvedResourceForSync, previewValue, readonlyValue]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: existing
    const isResourceAllowed = useMemo(() => {
      if (!resource) {
        return true;
      }

      if (inSampleProject) {
        return true;
      }

      const res = typeof resource === 'function' ? resource(props) : resource;

      if (Array.isArray(res)) {
        return res.some(r => isProjectResourceEnabled(r));
      }

      return isProjectResourceEnabled(res);
      // biome-ignore lint/correctness/useExhaustiveDependencies: existing
    }, [isProjectResourceEnabled, props, inSampleProject]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: existing
    const isFeatureEnabled = useMemo(() => {
      if (!feature) {
        return true;
      }

      const feat = typeof feature === 'function' ? feature(props) : feature;

      if (Array.isArray(feat)) {
        return feat.some(r => isFeatureOn(r));
      }

      return isFeatureOn(feat);
      // biome-ignore lint/correctness/useExhaustiveDependencies: existing
    }, [isFeatureOn, props]);

    // Don't show loader here - ProjectLoadingOverlay handles loading state
    if (
      (isFetchingProjectFeatures && resource) ||
      (isLoadingFeatures && feature)
    ) {
      return null;
    }

    // If permission data failed to load, throw so ErrorBoundary handles it
    // rather than running checks against empty data (which produces false 404/403)
    if (didProjectFeaturesError && resource) {
      throw new Error('Failed to load project permissions');
    }
    if (didFeaturesError && feature) {
      throw new Error('Failed to load feature flags');
    }

    if (!isFeatureEnabled) {
      return <ErrorPage type={ErrorStatus.NOT_FOUND} />;
    }

    if (!isResourceAllowed) {
      return <ErrorPage type={ErrorStatus.FORBIDDEN} />;
    }

    if (!isInternalUser) {
      const isTemplateEngineActive =
        isTemplateEngineUser || isTemplateEngineEnabled;

      if (isTemplateEngineActive) {
        const screenKey = options?.screenKey;

        if (screenKey) {
          if (!isTemplateEngineScreenAllowed(screenKey)) {
            return <ErrorPage type={ErrorStatus.NOT_FOUND} />;
          }
        } else {
          return <ErrorPage type={ErrorStatus.NOT_FOUND} />;
        }
      }
    }

    return <Component {...props} />;
  };

  return WrappedComponent;
}
